import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowTopRightOnSquareIcon, 
  PencilIcon, 
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { showSuccess, showError } from '../utils/notifications';
import API_CONFIG from '../config';
import { getOpenFoodFactsUrl } from '../services/openFoodFactsService';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchProductData = async () => {
      try {
        const productResponse = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!productResponse.ok) {
          if (productResponse.status === 401) {
            localStorage.removeItem('token');
            navigate('/login');
            return;
          } else if (productResponse.status === 404) {
            navigate('/products');
            return;
          }
        }
        
        const productData = await productResponse.json();
        setProduct(productData);
      } catch (error) {
        console.error("Erreur lors de la récupération du produit:", error);
        showError('Erreur lors du chargement du produit');
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [productId, navigate]);

  const handleEditProduct = () => {
    navigate(`/products/${productId}/edit`);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showSuccess('Produit supprimé avec succès');
        navigate('/products');
      } else {
        showError('Erreur lors de la suppression du produit');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showError('Erreur de connexion');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const goBack = () => {
    // Vérifier si on vient d'une catégorie
    const referrer = location.state?.from;
    if (referrer && referrer.startsWith('/categories/')) {
      navigate(referrer);
    } else {
      navigate('/products');
    }
  };

  const handleOpenFoodFactsClick = () => {
    if (hasValidBarcode) {
      const url = getOpenFoodFactsUrl(product.barcode);
      window.open(url, '_blank');
    } else {
      // Proposer d'ajouter un code-barres
      if (window.confirm('Ce produit n\'a pas de code-barres. Voulez-vous l\'ajouter maintenant ?')) {
        navigate(`/products/${productId}/edit`);
      }
    }
  };

  const hasValidBarcode = product && product.barcode && product.barcode.trim() !== '' && product.barcode !== null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Produit non trouvé</p>
          <button 
            onClick={goBack}
            className="mt-4 text-primary hover:text-primary-dark"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <button 
              onClick={goBack} 
                className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Retour
            </button>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{product.name}</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal - scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-40">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
            {/* Bouton de suppression en haut à droite */}
                      <button
              onClick={handleDeleteClick}
              className="absolute top-4 right-4 inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 transition-colors"
              title="Supprimer le produit"
                      >
              <TrashIcon className="w-4 h-4" />
                      </button>

            <h2 className="text-lg font-semibold text-gray-900 mb-6 pr-12">Informations du produit</h2>
            
            {/* Photo du produit */}
            {product.image_url && (
              <div className="mb-6">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full max-w-md h-64 object-cover rounded-lg border border-gray-200 shadow-sm"
                />
            </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Nom</dt>
                <dd className="mt-1 text-sm text-gray-900">{product.name}</dd>
              </div>
              {product.price && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Prix</dt>
                  <dd className="mt-1 text-xl font-bold text-primary">{product.price.toFixed(2)}€</dd>
                </div>
              )}
              {product.barcode && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Code-barres</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{product.barcode}</dd>
                </div>
              )}
              {product.unit_price && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Prix unitaire</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900">{product.unit_price.toFixed(2)}€</dd>
                </div>
              )}
              {product.unit && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Unité</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.unit}</dd>
                </div>
              )}
              {product.category && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Catégorie</dt>
                  <dd className="mt-1 text-sm text-gray-900">{product.category.name}</dd>
                </div>
              )}
              {product.description && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <div className={`whitespace-pre-wrap break-words ${!descriptionExpanded ? 'line-clamp-3' : ''}`}>
                      {product.description}
                    </div>
                    {(product.description.length > 150 || product.description.split('\n').length > 3) && (
                      <button
                        onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                        className="mt-2 inline-flex items-center text-primary hover:text-primary-dark text-sm font-medium"
                      >
                        {descriptionExpanded ? (
                          <>
                            <ChevronUpIcon className="w-4 h-4 mr-1" />
                            Voir moins
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="w-4 h-4 mr-1" />
                            Voir plus
                          </>
                        )}
                      </button>
                    )}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Date de création</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(product.created_at).toLocaleDateString('fr-FR')}
                </dd>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleEditProduct}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  title="Modifier le produit"
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  <span>Modifier le produit</span>
                </button>
                
                <button
                  onClick={handleOpenFoodFactsClick}
                  disabled={!hasValidBarcode}
                  className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors ${
                    hasValidBarcode
                      ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400' 
                      : 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
                  }`}
                  title={
                    hasValidBarcode
                      ? 'Voir sur OpenFoodFacts' 
                      : 'Cliquez pour ajouter un code-barres'
                  }
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
                  <span>
                    {hasValidBarcode ? 'Voir sur OpenFoodFacts' : 'Ajouter un code-barres'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le produit <strong>"{product.name}"</strong> ? 
              Cette action est irréversible.
            </p>
            
            <div className="flex justify-end space-x-3">
                  <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-sm font-medium rounded-lg text-white hover:bg-red-700 transition-colors"
                  >
                Supprimer
                  </button>
                </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail; 